import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS } from '../config/constants';
import './CRMContactCard.css';
import AppIcon from './icons/AppIcon';

export default function CRMContactCard({ card, onOpenCard, isDragging }) {
  const [contact, setContact] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);
  const [processedAvatarUrl, setProcessedAvatarUrl] = useState(null);

  useEffect(() => {
    if (card.entity_type === 'contact' && card.entity_id) {
      setAvatarError(false); // Reset avatar error quando contato muda
      setProcessedAvatarUrl(null); // Reset avatar URL
      loadContactData();
    }
  }, [card.entity_id]);

  const loadContactData = async () => {
    try {
      setLoading(true);

      // Buscar dados do contato
      const { data: contactData, error: contactError } = await supabase
        .from('contatos')
        .select('*')
        .eq('id', card.entity_id)
        .single();

      if (contactError) throw contactError;
      setContact(contactData);

      // Buscar última mensagem do contato
      const { data: conversaData } = await supabase
        .from('conversas')
        .select('id')
        .eq('contato_id', card.entity_id)
        .order('atualizado_em', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (conversaData) {
        const { data: messageData } = await supabase
          .from('mensagens')
          .select('conteudo, criado_em, direcao')
          .eq('conversa_id', conversaData.id)
          .order('criado_em', { ascending: false })
          .limit(1)
          .maybeSingle();

        setLastMessage(messageData);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do contato:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `(${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    return phone;
  };

  const getTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now - messageDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours}h`;
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `Há ${diffDays} dias`;
    return messageDate.toLocaleDateString('pt-BR');
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Normaliza URL de avatar (corrige protocolo quebrado e força https)
  const normalizeAvatarUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      let out = url.trim();
      // Corrige casos como "https:/pps.whatsapp.net" (1 barra só)
      if (/^https:\/pps\./i.test(out)) {
        out = out.replace(/^https:\/pps\./i, 'https://pps.');
      }
      if (/^http:\/pps\./i.test(out)) {
        out = out.replace(/^http:\/pps\./i, 'https://pps.');
      }
      // Força https quando protocolo http
      if (/^http:\/\//i.test(out)) {
        out = out.replace(/^http:\/\//i, 'https://');
      }
      return out;
    } catch (_) {
      return url || null;
    }
  };

  // Detecta se é URL pública do Supabase Storage
  const isPublicStorageUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return false;
      return /\/storage\/v1\/object\/public\//.test(url);
    } catch (_) { return false; }
  };

  // Detecta e extrai referência de Storage (bucket + path) de uma URL/caminho
  const parseStorageRef = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const raw = String(url).trim();
      // Rejeitar URLs externas (WhatsApp, Facebook, etc)
      if (/whatsapp\.net|fbcdn\.net|cdn\.|googleusercontent\.com|akamaihd\.net/i.test(raw)) return null;
      // URL padrão do Supabase Storage
      const m = raw.match(/\/storage\/v1\/object\/(public\/)?([^/]+)\/(.+)$/);
      if (m) {
        return { bucket: m[2], path: m[3] };
      }
      // Referência curta "bucket:path"
      if (raw.includes(':') && !raw.startsWith('http')) {
        const [bucket, ...rest] = raw.split(':');
        const path = rest.join(':').replace(/^\//, '');
        if (bucket && path) return { bucket, path };
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  // Obtém URL assinada do Storage via backend
  const getSignedUrl = async (bucket, path) => {
    try {
      const caminho_storage = `${bucket}:${path}`;
      const response = await fetch(API_ENDPOINTS.MEDIA_SIGNED_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caminho_storage, expiresIn: 3600 })
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.signedUrl || data?.url || null;
    } catch (_) {
      return null;
    }
  };

  // Processa URL do avatar (assina se necessário)
  const processAvatarUrl = async (url) => {
    if (!url) return null;
    const normalized = normalizeAvatarUrl(url);
    if (!normalized) return null;
    
    // Se é URL pública do Storage, usa direto
    if (isPublicStorageUrl(normalized)) {
      return normalized;
    }
    
    // Se é URL do Storage privado, assina
    const ref = parseStorageRef(normalized);
    if (ref) {
      const signed = await getSignedUrl(ref.bucket, ref.path);
      if (signed) return signed;
    }
    
    // Se é URL externa (WhatsApp, etc), usa direto com cache-buster
    if (/^https?:\/\//i.test(normalized)) {
      const sep = normalized.includes('?') ? '&' : '?';
      return `${normalized}${sep}v=${Math.floor(Date.now() / 60000)}`;
    }
    
    return normalized;
  };

  // Efeito para processar URL do avatar quando contato carrega
  useEffect(() => {
    if (contact?.image_url) {
      processAvatarUrl(contact.image_url).then(url => {
        setProcessedAvatarUrl(url);
      });
    }
  }, [contact?.image_url]);

  if (loading) {
    return (
      <div className="crm-contact-card loading" style={{ opacity: isDragging ? 0.5 : 1 }}>
        <div className="contact-loading">
          <AppIcon name="progress_activity" className="spinning" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className="crm-contact-card" 
      onClick={() => onOpenCard(card)}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {/* Header com foto e nome */}
      <div className="contact-header">
        <div className="contact-avatar">
          {processedAvatarUrl && !avatarError ? (
            <img 
              src={processedAvatarUrl} 
              alt={contact?.nome || 'Contato'} 
              onError={() => setAvatarError(true)}
              referrerPolicy="no-referrer"
            />
          ) : (
            <AppIcon name="person" />
          )}
        </div>
        <div className="contact-info">
          <h4 className="contact-name">{contact?.nome || card.title || 'Sem nome'}</h4>
          <p className="contact-phone">{formatPhoneNumber(contact?.numero)}</p>
        </div>
      </div>

      {/* Última mensagem */}
      {lastMessage && (
        <div className="contact-last-message">
          <div className="message-icon">
            <AppIcon name={lastMessage.direcao === 'entrada' ? 'chat_bubble' : 'send'} />
            {/* ✅ Badge de notificação para mensagens não respondidas */}
            {lastMessage.direcao === 'entrada' && (
              <span className="notification-badge" title="Mensagem não respondida">
                <AppIcon name="notifications_active" />
              </span>
            )}
          </div>
          <p className="message-text">{truncateText(lastMessage.conteudo)}</p>
        </div>
      )}

      {/* Footer com timestamp */}
      <div className="contact-footer">
        <div className="contact-time">
          <AppIcon name="schedule" />
          {getTimeAgo(lastMessage?.criado_em || card.created_at)}
        </div>

        {/* Badges adicionais */}
        <div className="contact-badges">
          {card.metadata?.source === 'whatsapp' && (
            <span className="contact-badge whatsapp">
              <AppIcon name="chat" />
            </span>
          )}
          {card.priority === 'high' && (
            <span className="contact-badge priority">
              <AppIcon name="priority_high" />
            </span>
          )}
        </div>
      </div>

      {/* Indicador de formulário pendente */}
      {card.metadata?.form_pending && (
        <div className="form-pending-indicator">
          <AppIcon name="assignment" />
          Formulário pendente
        </div>
      )}
    </div>
  );
}
